import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ReservationExportRow, UbyportLookup } from '../api.export';

const SIG_W = 150; // offscreen canvas px
const SIG_H = 48;
const SIG_PAD = 5;

/** Render an SVG path string onto an offscreen canvas and return a PNG data URL. */
function renderSignature(svgPath: string): string | null {
  if (!svgPath?.trim()) return null;
  const canvas = document.createElement('canvas');
  canvas.width = SIG_W;
  canvas.height = SIG_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, SIG_W, SIG_H);

  // Compute bounding box of path coordinates
  const nums = svgPath.match(/-?\d+(?:\.\d+)?/g);
  if (!nums || nums.length < 2) return canvas.toDataURL('image/png');

  const xs: number[] = [], ys: number[] = [];
  for (let i = 0; i + 1 < nums.length; i += 2) {
    xs.push(parseFloat(nums[i]));
    ys.push(parseFloat(nums[i + 1]));
  }
  const minX = Math.min(...xs), minY = Math.min(...ys);
  const bw = Math.max(...xs) - minX || 1;
  const bh = Math.max(...ys) - minY || 1;

  const scale = Math.min(
    (SIG_W - SIG_PAD * 2) / bw,
    (SIG_H - SIG_PAD * 2) / bh
  );
  const tx = SIG_PAD - minX * scale + ((SIG_W - SIG_PAD * 2) - bw * scale) / 2;
  const ty = SIG_PAD - minY * scale + ((SIG_H - SIG_PAD * 2) - bh * scale) / 2;

  ctx.setTransform(scale, 0, 0, scale, tx, ty);
  ctx.fillStyle = '#1a202c';
  try {
    ctx.fill(new Path2D(svgPath));
  } catch {
    // Path2D may fail on malformed paths — return blank canvas
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  return canvas.toDataURL('image/png');
}

function fmt(val: string | null | undefined): string {
  return val ?? '—';
}

// Columns (10 total):
// 0  #Res        12 mm
// 1  Guest Name  38 mm
// 2  Check-In    18 mm
// 3  Check-Out   18 mm
// 4  DOB         18 mm
// 5  Country     36 mm
// 6  Document    28 mm
// 7  Purpose     42 mm
// 8  ZTP          8 mm
// 9  Signature   32 mm   ← sigMap index

const SIG_COL_IDX = 9;
const SIG_COL_W = 32; // mm
const SIG_ROW_H = 9;  // mm — min row height

export function generateExportPdf(
  rows: ReservationExportRow[],
  apartmentName: string,
  from: string,
  to: string,
  lookup: UbyportLookup
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // ── Header ────────────────────────────────────────────────────────────────
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(`Guest Registration Export — ${apartmentName}`, 14, 12);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Period: ${from} to ${to}   |   Generated: ${new Date().toLocaleString()}`, 14, 18);

  const tableBody: string[][] = [];
  const sigMap = new Map<number, string>(); // rowIndex → PNG data URL

  for (const row of rows) {
    if (row.slots.length === 0) {
      tableBody.push([
        String(row.reservationId),
        '—', fmt(row.checkInDate), fmt(row.checkOutDate),
        '—', '—', '—', '—', '—', '',
      ]);
    } else {
      for (const slot of row.slots) {
        const rowIndex = tableBody.length;
        const sigDataUrl = slot.signatureSvg ? renderSignature(slot.signatureSvg) : null;
        if (sigDataUrl) sigMap.set(rowIndex, sigDataUrl);
        tableBody.push([
          String(row.reservationId),
          `${fmt(slot.firstName)} ${fmt(slot.surname)}`.trim() || '—',
          fmt(row.checkInDate),
          fmt(row.checkOutDate),
          fmt(slot.dateOfBirth),
          lookup.resolveCountry(slot.country),
          `${fmt(slot.documentType)} ${fmt(slot.documentNumber)}`.trim(),
          lookup.resolvePurpose(slot.purposeOfStay),
          slot.hasDisabilityCard ? 'Yes' : (slot.hasDisabilityCard === false ? 'No' : '—'),
          '',
        ]);
      }
    }
  }

  autoTable(doc, {
    startY: 22,
    head: [['#Res', 'Guest Name', 'Check-In', 'Check-Out', 'DOB', 'Country', 'Document', 'Purpose', 'ZTP', 'Signature']],
    body: tableBody,
    theme: 'grid',
    styles: { fontSize: 6.5, cellPadding: 1.5, overflow: 'linebreak', lineColor: [220, 220, 220] as [number, number, number], minCellHeight: SIG_ROW_H },
    headStyles: { fillColor: [44, 82, 130], textColor: 255, fontStyle: 'bold', fontSize: 6.5 },
    columnStyles: {
      0: { cellWidth: 12 },
      1: { cellWidth: 38 },
      2: { cellWidth: 18 },
      3: { cellWidth: 18 },
      4: { cellWidth: 18 },
      5: { cellWidth: 36 },
      6: { cellWidth: 28 },
      7: { cellWidth: 42 },
      8: { cellWidth: 8 },
      9: { cellWidth: SIG_COL_W },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === SIG_COL_IDX) {
        const sigUrl = sigMap.get(data.row.index);
        if (sigUrl) {
          const { x, y, width, height } = data.cell;
          const pad = 1;
          const imgW = width - pad * 2;
          const imgH = imgW * (SIG_H / SIG_W); // preserve 150:48 canvas ratio
          const yCenter = y + (height - imgH) / 2;
          doc.addImage(sigUrl, 'PNG', x + pad, yCenter, imgW, imgH);
        }
      }
    },
    rowPageBreak: 'avoid',
    margin: { left: 14, right: 14 },
  });

  // ── Page numbers ──────────────────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text(`Page ${i} of ${pageCount}`, pageW - 14, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
  }

  doc.save(`reservations-${from}-${to}.pdf`);
}
